import React, { useState } from "react";
import api, { extractError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarPlus, Send, Loader2, Truck, CheckCircle2, Check } from "lucide-react";
import { WASTE_OPTIONS, REQUEST_KINDS, wasteLabel } from "@/lib/format";
import { toast } from "sonner";

const todayStr = () => new Date().toISOString().split("T")[0];

export default function Requests() {
  // Collection request state
  const [date, setDate] = useState("");
  const [selectedWaste, setSelectedWaste] = useState([]);
  const [collNotes, setCollNotes] = useState("");
  const [submittingColl, setSubmittingColl] = useState(false);

  // General request state
  const [kind, setKind] = useState("");
  const [subject, setSubject] = useState("");
  const [reqNotes, setReqNotes] = useState("");
  const [submittingReq, setSubmittingReq] = useState(false);

  const toggleWaste = (w) => {
    setSelectedWaste((prev) => (prev.includes(w) ? prev.filter((x) => x !== w) : [...prev, w]));
  };

  const submitCollection = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Please choose a preferred date.");
    if (selectedWaste.length === 0) return toast.error("Please select at least one waste type.");
    setSubmittingColl(true);
    try {
      await api.post("/portal/request-collection", { requested_date: date, waste_types: selectedWaste, notes: collNotes || null });
      toast.success("Collection request submitted \u2014 our team will be in touch.");
      setDate("");
      setSelectedWaste([]);
      setCollNotes("");
    } catch (err) {
      toast.error(extractError(err, "Could not submit your request."));
    } finally {
      setSubmittingColl(false);
    }
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!kind) return toast.error("Please choose a request type.");
    setSubmittingReq(true);
    try {
      await api.post("/portal/requests", { kind, subject: subject || null, notes: reqNotes || null, payload: {} });
      toast.success("Request submitted \u2014 we'll get back to you shortly.");
      setKind("");
      setSubject("");
      setReqNotes("");
    } catch (err) {
      toast.error(extractError(err, "Could not submit your request."));
    } finally {
      setSubmittingReq(false);
    }
  };

  return (
    <div data-testid="requests-page">
      <PageHeader title="Requests" subtitle="Book an extra collection or ask us for a change to your service" />

      <Tabs defaultValue="collection" className="max-w-3xl">
        <TabsList className="mb-6">
          <TabsTrigger value="collection" data-testid="tab-request-collection"><CalendarPlus className="mr-1.5 h-4 w-4" /> Request a collection</TabsTrigger>
          <TabsTrigger value="general" data-testid="tab-general-request"><Send className="mr-1.5 h-4 w-4" /> Other request</TabsTrigger>
        </TabsList>

        {/* Collection request */}
        <TabsContent value="collection">
          <Card className="rounded-xl border bg-white p-6 shadow-[var(--shadow-sm)]">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--hh-amber-50)] text-[color:var(--hh-amber-600)]"><Truck className="h-5 w-5" /></span>
              <div><h3 className="font-display text-lg font-semibold text-[color:var(--hh-ink)]">Request an extra collection</h3><p className="text-sm text-muted-foreground">Tell us your preferred date and what needs collecting.</p></div>
            </div>
            <form onSubmit={submitCollection} className="space-y-6" data-testid="request-collection-form">
              <div className="space-y-2">
                <Label htmlFor="req-date">Preferred date</Label>
                <Input id="req-date" type="date" min={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} className="h-11 sm:max-w-xs" data-testid="request-collection-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Waste types</Label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {WASTE_OPTIONS.map((w) => {
                    const active = selectedWaste.includes(w);
                    return (
                      <div role="button" tabIndex={0} key={w} onClick={() => toggleWaste(w)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleWaste(w); } }} data-testid={`waste-option-${w}`} className={`flex cursor-pointer select-none items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors ${active ? "border-amber-300 bg-[var(--hh-amber-50)] text-[color:var(--hh-ink)]" : "border-[color:var(--hh-line)] bg-white text-[color:var(--hh-slate)] hover:bg-[var(--hh-band)]"}`}>
                        <span className={`flex h-4 w-4 flex-none items-center justify-center rounded border ${active ? "border-[var(--hh-amber-600)] bg-[var(--hh-amber-600)] text-white" : "border-[color:var(--hh-line)] bg-white"}`}>
                          {active && <Check className="h-3 w-3" strokeWidth={3} />}
                        </span>
                        {wasteLabel(w)}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="coll-notes">Notes (optional)</Label>
                <Textarea id="coll-notes" value={collNotes} onChange={(e) => setCollNotes(e.target.value)} placeholder="Any access instructions or extra details" rows={3} data-testid="request-collection-notes-input" />
              </div>
              <Button type="submit" disabled={submittingColl} className="h-11 rounded-xl bg-[hsl(var(--primary))] text-white hover:bg-[var(--hh-amber-600)]" data-testid="request-collection-submit">
                {submittingColl ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting</> : <><CheckCircle2 className="mr-2 h-4 w-4" /> Submit request</>}
              </Button>
            </form>
          </Card>
        </TabsContent>

        {/* General request */}
        <TabsContent value="general">
          <Card className="rounded-xl border bg-white p-6 shadow-[var(--shadow-sm)]">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--hh-amber-50)] text-[color:var(--hh-amber-600)]"><Send className="h-5 w-5" /></span>
              <div><h3 className="font-display text-lg font-semibold text-[color:var(--hh-ink)]">Submit a request</h3><p className="text-sm text-muted-foreground">Container changes, frequency updates, contact changes or support.</p></div>
            </div>
            <form onSubmit={submitRequest} className="space-y-6" data-testid="general-request-form">
              <div className="space-y-2">
                <Label>Request type</Label>
                <Select value={kind} onValueChange={setKind}>
                  <SelectTrigger className="h-11 sm:max-w-md" data-testid="general-request-kind-select"><SelectValue placeholder="Choose a request type" /></SelectTrigger>
                  <SelectContent>
                    {REQUEST_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="req-subject">Subject (optional)</Label>
                <Input id="req-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief summary" className="h-11 sm:max-w-md" data-testid="general-request-subject-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="req-notes">Details</Label>
                <Textarea id="req-notes" value={reqNotes} onChange={(e) => setReqNotes(e.target.value)} placeholder="Tell us what you need" rows={4} data-testid="general-request-notes-input" />
              </div>
              <Button type="submit" disabled={submittingReq} className="h-11 rounded-xl bg-[hsl(var(--primary))] text-white hover:bg-[var(--hh-amber-600)]" data-testid="general-request-submit">
                {submittingReq ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting</> : <><Send className="mr-2 h-4 w-4" /> Submit request</>}
              </Button>
            </form>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
